import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloProvider
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import React, { useContext } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ERROR_MESSAGES, errorKind } from 'src/lib/errors';
import { getAccessToken, reportSessionExpired } from 'src/lib/session';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI
});

// A fresh token per request (the Auth0 SDK serves it from cache and refreshes
// it before expiry), instead of the one captured at login that later expired.
// Header name and format unchanged.
const authLink = setContext(async (_, { headers }) => {
  const token = await getAccessToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  };
});

const isMutation = (operation: any) =>
  (operation.query?.definitions || []).some((d: any) => d.kind === 'OperationDefinition' && d.operation === 'mutation');

export const ERRORS = {
  '409': 'This user already exists'
};

export const getErrorMessage = (code: string): string => {
  for (const errorNum in ERRORS) {
    if (Object.prototype.hasOwnProperty.call(ERRORS, errorNum)) {
      if (code && code.includes(errorNum)) {
        return ERRORS[errorNum];
      }
    }
  }

  return 'Something went wrong';
};

export const ApolloCustomProvider = props => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Was: one generic "Something went wrong" toast per GraphQL error (including
  // loads the page already shows as an error state → two messages), network
  // errors only logged when a GraphQL error also existed, and an expired login
  // producing a toast storm with no way back.
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Full detail for developers only.
    console.error(`[API error] ${operation.operationName || 'anonymous'}`, { graphQLErrors, networkError });

    const kind = errorKind({ graphQLErrors, networkError });
    if (kind === 'session') {
      // One message on the login screen instead of a toast per request.
      reportSessionExpired();
      return;
    }

    const { handlesErrors, toastOnError } = operation.getContext();
    // Mutations toast unless the form shows the error itself; queries only when
    // the page asked for it (pages show their own "Couldn't load" + Retry).
    const toast = isMutation(operation) ? !handlesErrors : !!toastOnError;
    if (toast) {
      const legacy = getErrorMessage((graphQLErrors || []).map(e => e.message).join(' '));
      const message = legacy !== 'Something went wrong' ? legacy : ERROR_MESSAGES[kind];
      enqueueSnackbar(t(message), { variant: 'error', preventDuplicate: true });
    }
  });

  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URI
  });

  const _client = new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    cache: new InMemoryCache()
  });

  return <ApolloProvider client={_client}>{props.children}</ApolloProvider>;
};
